import { useMemo, useRef, useState, type DragEvent } from "react";
import { Link } from "react-router";
import { AlertCircle, CheckCircle2, FileSpreadsheet, Upload, X } from "lucide-react";
import type { Address, Import } from "@guestbook/shared";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { Button } from "@/shared/components/ui/Button";
import { SelectFormField } from "@/shared/components/form/SelectFormField";
import { SelectInput } from "@/shared/components/ui/SelectInput";
import { useScopedHref } from "@/shared/lib/scope";
import { useImportContacts } from "@/shared/lib/imports";
import {
  buildContactRows,
  columnLabel,
  CONTACT_FIELDS,
  guessMapping,
  NO_COLUMN,
  parseFile,
  toColumnMapping,
  type ColumnMapping,
  type ParsedFile,
} from "@/shared/lib/importParsing";
import { formatAddress } from "@/shared/lib/address";
import { cn } from "@/shared/lib/utils";

type Step = "upload" | "map" | "preview" | "done";

const PREVIEW_COLUMNS: { field: string; label: string }[] = [
  { field: "name", label: "Name" },
  { field: "email", label: "Email" },
  { field: "phone", label: "Phone" },
  { field: "address", label: "Mailing address" },
  { field: "tags", label: "Tags" },
];

/** Reads a value off a built row's `data` for the preview table. */
function cellValue(data: Record<string, unknown>, field: string): string {
  if (field === "tags") {
    const tags = data.tags;
    return Array.isArray(tags) ? tags.join(", ") : "";
  }
  if (field === "address") {
    return formatAddress(data.address as Partial<Address> | undefined) ?? "";
  }
  if (field.startsWith("address.")) {
    const address = data.address as Record<string, string> | undefined;
    return address?.[field.slice("address.".length)] ?? "";
  }
  const value = data[field];
  return typeof value === "string" ? value : "";
}

export function ContactImport() {
  const href = useScopedHref();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [step, setStep] = useState<Step>("upload");
  const [filename, setFilename] = useState("");
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [parseError, setParseError] = useState<string | null>(null);
  const [result, setResult] = useState<Import | null>(null);
  const [dragging, setDragging] = useState(false);

  const importContacts = useImportContacts();

  const builtRows = useMemo(
    () => (parsed ? buildContactRows(parsed, mapping) : []),
    [parsed, mapping],
  );
  const previewRows = builtRows.slice(0, 5);

  // Column-picker options: every CSV column plus a "not imported" choice.
  const columnOptions = useMemo(
    () => [
      { value: NO_COLUMN, label: "— Not imported —" },
      ...(parsed?.headers.map((_, i) => ({ value: String(i), label: columnLabel(parsed, i) })) ??
        []),
    ],
    [parsed],
  );

  // Every contact needs a name (contactInputSchema), so without a column
  // feeding it the whole import would be skipped — block the commit.
  const canImport = (mapping.name ?? []).some((id) => id !== NO_COLUMN);

  // Set the column at one slot of a field; collapse back to the real columns
  // (dropping cleared slots) so a field always holds [NO_COLUMN] or real ids.
  function setSlot(field: string, slotIndex: number, value: string) {
    setMapping((prev) => {
      const current = prev[field]?.length ? [...prev[field]] : [NO_COLUMN];
      current[slotIndex] = value;
      const real = current.filter((id) => id !== NO_COLUMN);
      return { ...prev, [field]: real.length > 0 ? real : [NO_COLUMN] };
    });
  }

  // Append an empty slot so the user can merge another column into the field.
  function addSlot(field: string) {
    setMapping((prev) => {
      const current = prev[field]?.length ? prev[field] : [NO_COLUMN];
      return { ...prev, [field]: [...current, NO_COLUMN] };
    });
  }

  // Drop a merge slot entirely (used by the per-slot remove control).
  function removeSlot(field: string, slotIndex: number) {
    setMapping((prev) => {
      const current = prev[field]?.length ? [...prev[field]] : [NO_COLUMN];
      current.splice(slotIndex, 1);
      const real = current.filter((id) => id !== NO_COLUMN);
      return { ...prev, [field]: real.length > 0 ? real : [NO_COLUMN] };
    });
  }

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setParseError(null);
    try {
      const next = await parseFile(file);
      setParsed(next);
      setMapping(guessMapping(next.headers));
      setFilename(file.name);
      setStep("map");
    } catch (err) {
      setParsed(null);
      setStep("upload");
      setParseError(err instanceof Error ? err.message : "Could not read that file.");
    }
  }

  function reset() {
    setStep("upload");
    setParsed(null);
    setMapping({});
    setFilename("");
    setParseError(null);
    setResult(null);
    importContacts.reset();
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    void handleFile(event.dataTransfer.files?.[0]);
  }

  function commit() {
    const columnMapping = parsed ? toColumnMapping(parsed, mapping) : {};
    importContacts.mutate(
      { filename, columnMapping, rows: builtRows },
      {
        onSuccess: (imported) => {
          setResult(imported);
          setStep("done");
        },
      },
    );
  }

  const chooseFileButton = (
    <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
      <Upload aria-hidden className="h-4 w-4" />
      Choose a different file
    </Button>
  );

  return (
    <Section spacing="md">
      <Container size="4xl">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <Link
              to={href("/app/contacts")}
              className="type-label-sm tracking-wider uppercase text-text-muted hover:text-text"
            >
              ← Back to contacts
            </Link>
            <h1 className="type-heading-lg text-text">Import contacts</h1>
            <p className="type-body-sm text-text-muted">
              Upload a CSV or Excel (.xlsx) file, match its columns to contact
              fields, and preview before importing.
            </p>
          </div>

          {/* Hidden input drives every "choose file" affordance. */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(event) => {
              void handleFile(event.target.files?.[0]);
              // Allow re-selecting the same filename later.
              event.target.value = "";
            }}
          />

          {step === "upload" ? (
            <div className="flex flex-col gap-4">
              <div
                role="button"
                tabIndex={0}
                aria-label="Upload a CSV or Excel file"
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                className={cn(
                  "flex cursor-pointer flex-col items-center gap-4 rounded-xl border-2 border-dashed border-border p-12 text-center transition-colors duration-150",
                  "glass-surface-subtle hover:bg-surface-muted/40",
                  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
                  dragging && "border-brand bg-surface-muted/40",
                )}
              >
                <FileSpreadsheet aria-hidden className="h-10 w-10 text-text-muted" />
                <div className="flex flex-col gap-1">
                  <p className="type-body-base text-text">
                    Drag a file here, or click to choose
                  </p>
                  <p className="type-body-sm text-text-muted">CSV or Excel (.xlsx)</p>
                </div>
              </div>
              {parseError ? (
                <p className="mx-4 type-body-sm text-danger-strong" role="alert">
                  {parseError}
                </p>
              ) : null}
            </div>
          ) : null}

          {step === "map" && parsed ? (
            <div className="flex flex-col gap-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="type-body-sm text-text-muted">
                  Found {parsed.headers.length} columns in{" "}
                  <span className="text-text">{filename}</span>. Choose which
                  column fills each contact field.
                </p>
                {chooseFileButton}
              </div>

              {/* One row per contact field; the dropdown(s) pick which CSV
                  column(s) feed it. Unmapped fields read "Not imported" so
                  it's obvious what the file is missing. */}
              <div className="flex flex-col gap-5">
                {CONTACT_FIELDS.map((field) => {
                  const assigned = mapping[field.value];
                  const slots = assigned && assigned.length > 0 ? assigned : [NO_COLUMN];
                  const required = field.value === "name";
                  const lastFilled = slots[slots.length - 1] !== NO_COLUMN;
                  return (
                    <div key={field.value} className="flex flex-col gap-2">
                      <SelectFormField
                        label={field.label}
                        options={columnOptions}
                        value={slots[0]}
                        onValueChange={(value) => setSlot(field.value, 0, value)}
                        hint={
                          required
                            ? "Required — every contact needs a name."
                            : undefined
                        }
                      />
                      {slots.slice(1).map((slot, i) => (
                        <div key={i + 1} className="flex items-center gap-2">
                          <SelectInput
                            aria-label={`${field.label} — merge column ${i + 2}`}
                            options={columnOptions}
                            value={slot}
                            onValueChange={(value) => setSlot(field.value, i + 1, value)}
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Remove merge column ${i + 2} from ${field.label}`}
                            onClick={() => removeSlot(field.value, i + 1)}
                          >
                            <X aria-hidden className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      {lastFilled ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="self-start"
                          onClick={() => addSlot(field.value)}
                        >
                          + Merge another column
                        </Button>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              {!canImport ? (
                <p className="mx-4 type-body-sm text-warn-strong" role="alert">
                  Map a column to Name to continue — every contact needs a name.
                </p>
              ) : null}

              <div className="flex flex-row-reverse gap-2">
                <Button onClick={() => setStep("preview")} disabled={!canImport}>
                  Preview import
                </Button>
                <Button asChild variant="ghost">
                  <Link to={href("/app/contacts")}>Cancel</Link>
                </Button>
              </div>
            </div>
          ) : null}

          {step === "preview" && parsed ? (
            <div className="flex flex-col gap-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="type-body-sm text-text-muted">
                  Showing the first {previewRows.length} of {builtRows.length}{" "}
                  contacts from <span className="text-text">{filename}</span>
                </p>
                {chooseFileButton}
              </div>

              {/* Mirrors the export page's preview table: a real <table> in an
                  overflow-x-auto wrapper with nowrap cells, so long values like
                  the full address keep their column aligned and scroll
                  horizontally rather than wrapping or clipping. */}
              <div className="overflow-x-auto rounded-xl border border-border-muted glass-surface-subtle">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b border-border-muted">
                      {PREVIEW_COLUMNS.map((col) => (
                        <th
                          key={col.field}
                          className="whitespace-nowrap px-4 py-2.5 type-label-sm uppercase tracking-wider text-text-muted"
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row) => (
                      <tr
                        key={row.rowNumber}
                        className="border-t border-border-muted first:border-t-0"
                      >
                        {PREVIEW_COLUMNS.map((col) => (
                          <td
                            key={col.field}
                            className="whitespace-nowrap px-4 py-2.5 type-body-sm text-text"
                          >
                            {cellValue(row.data, col.field) || (
                              <span className="text-text-muted">—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {importContacts.error ? (
                <p className="mx-4 type-body-sm text-danger-strong" role="alert">
                  {importContacts.error.message}
                </p>
              ) : null}

              <div className="flex flex-row-reverse gap-2">
                <Button onClick={commit} disabled={importContacts.isPending}>
                  {importContacts.isPending
                    ? "Importing…"
                    : `Import ${builtRows.length} contacts`}
                </Button>
                <Button variant="ghost" onClick={() => setStep("map")}>
                  Back to mapping
                </Button>
              </div>
            </div>
          ) : null}

          {step === "done" && result ? (
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center gap-3 rounded-xl glass-surface-subtle p-8 text-center">
                {result.status === "committed" ? (
                  <CheckCircle2 aria-hidden className="h-10 w-10 text-brand" />
                ) : (
                  <AlertCircle aria-hidden className="h-10 w-10 text-danger-strong" />
                )}
                <p className="type-heading-sm text-text">
                  Imported {result.summary?.created ?? 0} contacts
                </p>
                {result.summary && result.summary.skipped > 0 ? (
                  <p className="type-body-sm text-text-muted">
                    {result.summary.skipped} rows were skipped.
                  </p>
                ) : null}
              </div>

              {result.summary && result.summary.errors.length > 0 ? (
                <div className="flex flex-col gap-2 rounded-xl glass-surface-subtle p-4">
                  <p className="type-label-sm tracking-wider uppercase text-text-muted">
                    Skipped rows
                  </p>
                  <ul className="flex flex-col gap-1">
                    {result.summary.errors.slice(0, 20).map((error) => (
                      <li key={error.row} className="type-body-sm text-text">
                        Row {error.row}: <span className="text-text-muted">{error.message}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="flex flex-row-reverse gap-2">
                <Button asChild>
                  <Link to={href("/app/contacts")}>Back to contacts</Link>
                </Button>
                <Button variant="ghost" onClick={reset}>
                  Import another file
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </Container>
    </Section>
  );
}
